import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, take, map } from 'rxjs/operators';
import { SiteService } from '../services/site.service';
import { PlatformService } from '../services/platform.service';
import { Site } from '../models/site.model';
import { environment } from '../../../environments/environment';

/**
 * DemoGateGuard
 *
 * Front-end-only single-password gate. SCOPED to specific site IDs in
 * environment.demoGateSiteIds — does NOT affect any other tenant. Use to
 * lock down a demo / WIP site you want to share with a controlled audience.
 *
 * Currently disabled (demoGateSiteIds is empty). To activate for a future
 * demo, add the target idSite(s) to environment.demoGateSiteIds.
 *
 * Storage: localStorage with a versioned key. Once unlocked, stays unlocked
 * across tabs, windows, and browser restarts. Only clears when:
 *   - User clears their browser site data / cache for this domain, OR
 *   - You bump the DEMO_GATE_VERSION constant below (forces re-prompt for
 *     everyone the next time they visit; use this if you rotate the
 *     password and want to invalidate the previous unlock).
 *
 * To disable entirely: set environment.demoGatePassword = '' OR set
 * environment.demoGateSiteIds = [].
 */
const DEMO_GATE_STORAGE_KEY = 'lp_demo_gate_unlocked_v1';
const DEMO_GATE_RETURN_KEY = 'lp_demo_gate_return_url';

@Injectable({ providedIn: 'root' })
export class DemoGateGuard implements CanActivate {
  constructor(
    private router: Router,
    private siteService: SiteService,
    private platformService: PlatformService,
  ) {}

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {
    const password = (environment as any).demoGatePassword;
    const gatedSiteIds: number[] = (environment as any).demoGateSiteIds || [];
    // No password configured OR no site IDs listed → guard is a no-op.
    if (!password || gatedSiteIds.length === 0) return true;
    // Already unlocked. localStorage persists across tabs / windows / browser
    // restarts, only clears when the user clears site data or we rotate
    // DEMO_GATE_STORAGE_KEY. No localStorage on the server — SSR always
    // re-evaluates below (there is no prior visit to have unlocked).
    if (this.platformService.isBrowser && localStorage.getItem(DEMO_GATE_STORAGE_KEY) === 'true') return true;

    const site = this.siteService.getCurrentSite();
    if (site) return this.evaluate(site, gatedSiteIds, state);

    return this.siteService.site$.pipe(
      filter(s => s !== null),
      take(1),
      map(s => this.evaluate(s!, gatedSiteIds, state))
    );
  }

  private evaluate(site: Site, gatedSiteIds: number[], state: RouterStateSnapshot): boolean | UrlTree {
    // Site is not in the gated list → pass through. THIS is the safety
    // valve that keeps every other tenant unaffected.
    if (!gatedSiteIds.includes(site.idSite)) return true;
    // Site IS gated. Remember return URL and route to the unlock screen.
    // Return URL uses sessionStorage on purpose - we only need it for the
    // immediate redirect after entering the password. No-op on the server —
    // there is no browser tab coming back to consume this value.
    if (this.platformService.isBrowser) {
      sessionStorage.setItem(DEMO_GATE_RETURN_KEY, state.url);
    }
    return this.router.createUrlTree(['/demo-unlock']);
  }
}
